package com.evoapps.evoshift.domain.market.payroll.poland

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.MonthWorkInput
import com.evoapps.evoshift.domain.market.payroll.PayLine
import com.evoapps.evoshift.domain.market.payroll.PayLineKind
import com.evoapps.evoshift.domain.market.payroll.PayrollFact
import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.market.payroll.Payslip
import com.evoapps.evoshift.domain.market.payroll.PayslipInput
import com.evoapps.evoshift.domain.market.payroll.PayslipNote
import com.evoapps.evoshift.domain.market.payroll.ShiftPay
import com.evoapps.evoshift.domain.market.payroll.ShiftPayInput
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.SystemContractType
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToLong

/**
 * Polish payroll: employer contributions (ZUS, FP, FGŚP), the employee's contributions, health
 * insurance and the monthly income-tax advance, for employment (UoP), civil-law (zlecenie) and
 * specific-task (dzieło) contracts; contractors (B2B) invoice and carry their own taxes; the
 * owner or a partner without a contract (SH-133) costs only an optional notional hourly amount.
 *
 * Overtime, Sunday and holiday supplements and the minimum-wage top-up are worked out per month
 * by [monthFactors] (SH-57, [PolishWorkingTime]), sick pay, holiday pay and pay terms that change
 * during the month by [PolishLeavePay]; PPK in the [payslip]. Simplifications, documented in
 * docs/user/payroll-poland.md: no copyright costs, one payer at a time.
 * A contract without a type is costed as employment, the most expensive common case.
 */
object PolandPayroll : PayrollRules {
    override val market = Market.POLAND

    /**
     * SH-212: PIT-0 for young people (ulga dla młodych, art. 21 ust. 1 pkt 148 ustawy o PIT), the
     * monthly tax-free reduction (PIT-2) and the higher costs of earning income for commuting.
     * The other PIT-0 reliefs (returning from abroad, families of 4+, working seniors) are not
     * modelled by this payroll yet.
     */
    override val taxReliefs = listOf(PayrollFact.UNDER_26, PayrollFact.TAX_RELIEF, PayrollFact.COMMUTES)

    override fun workingHours(year: Int, month: Int) = PolishCalendar.workingHours(year, month)

    override fun workingHours(start: LocalDate, endInclusive: LocalDate) = PolishCalendar.workingHours(start, endInclusive)

    override fun nightHours(start: LocalDateTime, end: LocalDateTime, settings: WorkplacePayrollSettings) =
        nightMinutes(start, end, settings) / 60.0

    override fun isHoliday(date: LocalDate) = PolishCalendar.isHoliday(date)

    override fun monthFactors(input: MonthWorkInput): MonthPayFactors = PolishWorkingTime.monthFactors(input)

    /** SH-113: 20 days, 26 after 10 years of work (Kodeks pracy art. 154 § 1), on an employment contract only. */
    override fun annualLeaveDays(contract: SystemContractType, profile: PayrollProfile, year: Int): Int? {
        if (contract != SystemContractType.STANDARD_EMPLOYMENT) return null
        val rates = PolandRates.forYear(year)
        return if (profile.longService) rates.leaveDaysLongService else rates.leaveDays
    }

    override fun onDemandLeaveDays(year: Int): Int = PolandRates.forYear(year).onDemandLeaveDays

    override fun shiftPay(input: ShiftPayInput): ShiftPay {
        val rates = PolandRates.forYear(input.start.year)
        val norm = workingHours(input.start.year, input.start.month.ordinal + 1).toDouble()
        // SH-116: unpaid breaks are not working time: neither hours nor night hours.
        val unpaid = input.unpaidBreaks.map { maxOf(it.start, input.start) to minOf(it.end, input.end) }.filter { it.second > it.first }
        val hours = (minutesBetween(input.start, input.end) - unpaid.sumOf { minutesBetween(it.first, it.second) }).coerceAtLeast(0) / 60.0
        val hourlyRate = when (val pay = input.compensation) {
            is CompensationModel.Hourly -> pay.rate
            // The salary is for the person's FTE: a 0.5 FTE salary pays half the norm's hours.
            is CompensationModel.MonthlyFixed -> pay.baseSalary / (norm * pay.fte.takeIf { it > 0 }.orOne())
        }
        val basePay = hours * hourlyRate
        val nightHours = (nightMinutes(input.start, input.end, input.settings) -
            unpaid.sumOf { nightMinutes(it.first, it.second, input.settings) }).coerceAtLeast(0) / 60.0
        val nightAllowance = if (input.contract.isEmployment()) {
            nightHours * rates.nightAllowanceRate * rates.minimumWage / norm
        } else 0.0
        val gross = money(basePay + nightAllowance)

        // Employer contributions are linear in the gross, except for thresholds that depend on the
        // whole month (Labour Fund from the minimum wage), so the rate comes from the expected month.
        val month = input.expectedMonthlyGross ?: when (val pay = input.compensation) {
            is CompensationModel.Hourly -> pay.rate * norm
            is CompensationModel.MonthlyFixed -> pay.baseSalary
        }
        val employerRate = if (month > 0) {
            payslip(
                PayslipInput(input.start.year, input.start.month.ordinal + 1, input.contract, input.profile, month, input.settings)
            ).employerContributions / month
        } else 0.0
        val contributions = money(gross * employerRate)
        return ShiftPay(
            hours = hours,
            hourlyRate = hourlyRate,
            basePay = money(basePay),
            nightHours = nightHours,
            nightAllowance = money(nightAllowance),
            gross = gross,
            employerContributions = contributions,
            employerCost = money(gross + contributions)
        )
    }

    override fun payslip(input: PayslipInput): Payslip {
        val rates = PolandRates.forYear(input.year)
        val notes = mutableSetOf<PayslipNote>()
        if (!PolandRates.isExact(input.year)) notes += PayslipNote.RATES_ESTIMATED
        val gross = money(input.gross)
        val profile = input.profile

        val contract = if (input.contract == SystemContractType.NOT_SET) SystemContractType.STANDARD_EMPLOYMENT else input.contract
        if (contract == SystemContractType.B2B_CONTRACTOR) {
            return Payslip(gross, emptyList(), emptyList(), net = gross, employerCost = gross, notes = notes + PayslipNote.CONTRACTOR)
        }
        // SH-133: no contract, no contributions; the cost is the notional amount, if any.
        if (contract == SystemContractType.OWNER_PARTNER) {
            return Payslip(gross, emptyList(), emptyList(), net = gross, employerCost = gross, notes = notes + PayslipNote.OWNER_PARTNER)
        }

        // Which insurances apply.
        val social: Boolean
        val sickness: Boolean
        val healthDue: Boolean
        when (contract) {
            SystemContractType.STANDARD_EMPLOYMENT -> { social = true; sickness = true; healthDue = true }
            SystemContractType.CIVIL_LAW_CONTRACT -> when {
                profile.student -> { social = false; sickness = false; healthDue = false; notes += PayslipNote.STUDENT_EXEMPT }
                profile.insuredElsewhereAtMinimumWage -> {
                    social = false; sickness = false; healthDue = true; notes += PayslipNote.INSURED_ELSEWHERE
                }
                else -> { social = true; sickness = profile.voluntarySickness; healthDue = true }
            }
            else -> { social = false; sickness = false; healthDue = false } // specific-task contract
        }

        // SH-57: sick pay and the ZUS sickness benefit carry no social contributions.
        val socialGross = max(0.0, gross - money(input.contributionFreeGross))

        // Pension and disability stop at the annual cap; the other contributions do not.
        val cappedBase = if (social) {
            val left = max(0.0, rates.pensionDisabilityCap - input.grossSoFarThisYear)
            min(socialGross, left).also { if (it < socialGross) notes += PayslipNote.CONTRIBUTION_CAP_REACHED }
        } else 0.0

        // SH-57: PPK on the pension base without the cap, where the pension insurance is due.
        val ppk = social && profile.ppk && socialGross > 0
        val ppkReduced = ppk && profile.ppkReducedRate && gross <= rates.ppkReducedIncomeFactor * rates.minimumWage
        if (ppkReduced) notes += PayslipNote.PPK_REDUCED
        val ppkEmployee = if (ppk) money(socialGross * if (ppkReduced) rates.ppkEmployeeReduced else rates.ppkEmployee) else 0.0
        val ppkEmployer = if (ppk) money(socialGross * rates.ppkEmployer) else 0.0

        val employee = buildList {
            if (social) {
                add(PayLine(PayLineKind.PENSION, money(cappedBase * rates.pensionEmployee)))
                add(PayLine(PayLineKind.DISABILITY, money(cappedBase * rates.disabilityEmployee)))
            }
            if (sickness) add(PayLine(PayLineKind.SICKNESS, money(socialGross * rates.sicknessEmployee)))
        }
        val employeeSocial = employee.sumOf { it.amount }
        val health = if (healthDue) money((gross - employeeSocial) * rates.health) else 0.0

        // The employer's PPK contribution is the employee's taxable revenue (not paid out).
        val tax = incomeTax(contract, profile, gross + ppkEmployer, employeeSocial, input.grossSoFarThisYear, rates, notes)

        val employer = buildList {
            if (social) {
                add(PayLine(PayLineKind.PENSION, money(cappedBase * rates.pensionEmployer)))
                add(PayLine(PayLineKind.DISABILITY, money(cappedBase * rates.disabilityEmployer)))
                add(PayLine(PayLineKind.ACCIDENT, money(socialGross * input.settings.accidentInsuranceRate)))
                // Labour Fund only from a month's pay of at least the minimum wage.
                if (gross >= rates.minimumWage) add(PayLine(PayLineKind.LABOUR_FUND, money(cappedBase * rates.labourFund)))
                add(PayLine(PayLineKind.GUARANTEE_FUND, money(cappedBase * rates.guaranteeFund)))
            }
            if (ppk) add(PayLine(PayLineKind.PPK, ppkEmployer))
        }

        val employeeLines = employee + listOfNotNull(
            PayLine(PayLineKind.HEALTH, health).takeIf { healthDue },
            PayLine(PayLineKind.INCOME_TAX, tax),
            PayLine(PayLineKind.PPK, ppkEmployee).takeIf { ppk }
        )
        val net = money(gross - employeeLines.sumOf { it.amount })
        // What ZUS pays (the sickness benefit) is not the employer's cost.
        return Payslip(
            gross = gross,
            employeeLines = employeeLines,
            employerLines = employer,
            net = net,
            employerCost = money(gross - money(input.paidByOthers) + employer.sumOf { it.amount }),
            notes = notes
        )
    }

    /** The monthly tax advance, in whole złoty. */
    private fun incomeTax(
        contract: SystemContractType,
        profile: PayrollProfile,
        gross: Double,
        employeeSocial: Double,
        grossSoFar: Double,
        rates: PolandRates,
        notes: MutableSet<PayslipNote>
    ): Double {
        if (gross <= 0) return 0.0
        // Under 26: revenue from employment and civil-law contracts is exempt up to the annual limit.
        val youth = profile.under26 && contract != SystemContractType.SPECIFIC_TASK_CONTRACT
        val exempt = if (youth) min(gross, max(0.0, rates.youthReliefLimit - grossSoFar)) else 0.0
        if (exempt > 0) notes += PayslipNote.YOUTH_RELIEF
        val taxableShare = (gross - exempt) / gross
        if (taxableShare <= 0.0) return 0.0

        val revenue = gross * taxableShare
        val social = employeeSocial * taxableShare
        val costs = when (contract) {
            SystemContractType.STANDARD_EMPLOYMENT ->
                if (profile.commutes) rates.employmentCostsCommuting else rates.employmentCosts
            SystemContractType.CIVIL_LAW_CONTRACT -> rates.civilCostsRate * (revenue - social)
            else -> rates.civilCostsRate * revenue
        }
        val base = wholeZloty(max(0.0, revenue - social - costs))
        // The payer does not know the person's other income: the higher rate applies once this
        // payer's taxable income for the year passes the threshold (approximated from the gross).
        val baseSoFar = max(0.0, grossSoFar * (1 - rates.pensionEmployee - rates.disabilityEmployee) - costs)
        val atLowerRate = max(0.0, min(base, rates.higherTaxThreshold - baseSoFar))
        val beforeReduction = atLowerRate * rates.taxRate + (base - atLowerRate) * rates.higherTaxRate
        val reduction = if (profile.taxReliefRequested) rates.monthlyTaxReduction else 0.0
        return wholeZloty(max(0.0, beforeReduction - reduction))
    }

    private fun SystemContractType.isEmployment() =
        this == SystemContractType.STANDARD_EMPLOYMENT || this == SystemContractType.NOT_SET

    /** Minutes of [start, end) inside the workplace's night period. */
    internal fun nightMinutes(start: LocalDateTime, end: LocalDateTime, settings: WorkplacePayrollSettings): Long {
        if (end <= start) return 0
        val nightStart = LocalTime.parse(settings.nightStart)
        val nightEnd = LocalTime.parse(settings.nightEnd)
        var total = 0L
        var day: LocalDate = start.date.minus(DatePeriod(days = 1))
        while (day <= end.date) {
            val windowStart = day.atTime(nightStart)
            val windowEnd = if (nightEnd <= nightStart) day.plus(DatePeriod(days = 1)).atTime(nightEnd) else day.atTime(nightEnd)
            val from = maxOf(start, windowStart)
            val to = minOf(end, windowEnd)
            if (to > from) total += minutesBetween(from, to)
            day = day.plus(DatePeriod(days = 1))
        }
        return total
    }

    private fun minutesBetween(from: LocalDateTime, to: LocalDateTime): Long =
        (to.toInstant(TimeZone.UTC) - from.toInstant(TimeZone.UTC)).inWholeMinutes

    private fun Double?.orOne() = this ?: 1.0

    /** Rounded to grosze, half up. */
    internal fun money(value: Double): Double = (value * 100).roundToLong() / 100.0

    /** Tax amounts and bases are rounded to whole złoty (Ordynacja podatkowa art. 63). */
    private fun wholeZloty(value: Double): Double = value.roundToLong().toDouble()
}
